import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { QueryParams } from "./QueryParams";
import { JSX } from "react";
import { IAssetWorXPlusTreeViewItem, IAssetWorXPlusTreeViewItemWithChildren, IRouterProps, withRouting } from "@iconics/components";
import { QueryParamsHelper } from "../../../../Common/QueryParamsHelper";
import { AssetWorXPlusTreeView } from "@iconics/components";
import { LanguageValuesHelper } from "@iconics/localisation";
import { Card, ConfirmDialog, GridContainer, GridItem, LayoutContainer, LayoutContent } from "@iconics/components";
import { Guid, PagedResponse } from "@iconics/core";
import { ApiConstants } from "../../../../Providers.Api/ApiConstants";
import { SerendieSymbol } from "@serendie/symbols";
import { NodeData } from "./DataModels";
import { RootNodeId } from "../../../../Providers.StaticData/StaticDataProvider";
import { NodesImportDialog } from "./NodesImportDialog";

export class NodesTreeView extends IbssPage<INodesTreeViewProps, INodesTreeViewState>
{
    private queryParams = new QueryParamsHelper(new QueryParams(), firstLoad => this.handleQueryParamsUpdated(firstLoad), this.props.navigate);
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: INodesTreeViewProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            nodes: [],
            importNodeId: 0,
            importNodes: [],
            showConfirmDelete: false,
            showImportDialog: false,
            refreshTreeToken: Guid.new().toString(),
        };
    }

    public async componentDidMount()
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_AssetWorX_MenuTitle;
        this.queryParams.evaluate();
    }

    public componentDidUpdate(prevProps: INodesTreeViewProps)
    {
        this.queryParams.evaluate(this.props.location, prevProps.location);
    }

    public async handleQueryParamsUpdated(firstLoad: boolean)
    {
        const { expandedNodeId } = this.queryParams.value;
        if (expandedNodeId && firstLoad)
        {
            this.setState({ expandedNodeId: expandedNodeId });
        }
    }

    private async handleGetNodes(parentId?: number): Promise<IAssetWorXPlusTreeViewItem[]>
    {
        try
        {
            const filter = (parentId ? `Parent_ID eq ${parentId}` : `ID eq ${RootNodeId}`);

            const nodesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.get<PagedResponse<NodeData[]>>({
                select: NodeData,
                tenancy: appContext().tenantId,
                filter: `${filter} and Deleted eq false`,
                orderBy: 'SortOrder asc',
                top: ApiConstants.largeODataTop,
            });

            const nodes = nodesData
                .value
                .map(i => ({
                    id: i.ID,
                    parentId: i.Parent_ID ?? undefined,
                    name: this.languageValuesHelper.getValue(this.languageValuesHelper.fromString(i.DisplayName)) || i.Name,

                })) satisfies IAssetWorXPlusTreeViewItem[];

            return nodes;
        }
        catch
        {
            return [];
        }
    }

    private async handleGetAncestorNodes(nodeId: number): Promise<number[]>
    {
        try
        {
            let ancestorIds = [nodeId];
            do
            {
                const ancestorData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.get<PagedResponse<NodeData[]>>({
                    select: NodeData,
                    tenancy: appContext().tenantId,
                    filter: `ID eq ${ancestorIds[0]} and Deleted eq false`,
                    top: 1,
                });

                const ancestor = ancestorData.value[0];
                if (!ancestor.Parent_ID)
                {
                    break;
                }
                ancestorIds = [ancestor.Parent_ID, ...ancestorIds];
            }
            while (true);

            return ancestorIds;
        }
        catch
        {
            return [];
        }
    }

    private handleAdd(node: IAssetWorXPlusTreeViewItem): void
    {
        this.props.navigate(`/admin/nodes/${node.id}/children/new`);
    }

    private handleEdit(node: IAssetWorXPlusTreeViewItem): void
    {
        this.props.navigate(`/admin/nodes/${node.id}/edit`);
    }

    private async handleImport(node: IAssetWorXPlusTreeViewItem): Promise<void>
    {
        await this.setStateAsync({ 
            selectedNode: node });

        const label = this.buildNodePath(this.state.nodes, node.id);

        await this.setState({
            showImportDialog: true,
            importNodes: [{ label: label, id: node.id}],
            importNodeId: node.id
        });
    }
    private buildNodePath(nodes: IAssetWorXPlusTreeViewItemWithChildren[], nodeId: number): string
    {
        const found = nodes.find(x => x.id === nodeId);

        if (found)
        {
            return found.name;
        }

        for (const node of nodes)
        {
            if (!node.children)
                continue;
            
            const foundInChildren = this.buildNodePath(node.children, nodeId);
            if (foundInChildren)
                return `${node.name} > ${foundInChildren}`;
        }
        return '';
    }

    private handleAlertClose(): void
    {
        this.setState({
            showImportDialog: false,
            refreshTreeToken: Guid.new().toString(),
        });
    }
    private handleTryDelete(node: IAssetWorXPlusTreeViewItem): void
    {
        this.setState({ showConfirmDelete: true, selectedNode: node });
    }

    private async onDeleteConfirm(): Promise<void> 
    {
        await this.loading(async () => 
        {
            if (this.state.selectedNode)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.delete({
                    nodeId: this.state.selectedNode.id,
                    tenancy: appContext().tenantId,
                });

                const updatedNodes = this.removeNodeById(this.state.nodes, this.state.selectedNode.id);
                this.setState({ nodes: updatedNodes });
            }
        });
        this.setState({ showConfirmDelete: false });
    }

    private removeNodeById(nodes: IAssetWorXPlusTreeViewItemWithChildren[], idToRemove: number): IAssetWorXPlusTreeViewItemWithChildren[]
    {
        return nodes
            .filter(i => i.id !== idToRemove)
            .map(node =>
            {
                return {
                    ...node,
                    children: (node.children && this.removeNodeById(node.children, idToRemove)),
                };
            });
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels

        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            icon={<SerendieSymbol name='layer' />}
                            title={labels.Platform_AssetWorX_NodeHierarchyTitle}
                            subTitle={labels.Platform_AssetWorX_NodeHierarchySubTitle}
                        >
                            <GridContainer>
                                <GridItem
                                    style={{ padding: '24px' }}
                                >
                                    <AssetWorXPlusTreeView
                                        nodes={this.state.nodes}
                                        onNodesChange={i => this.setStateAsync({ nodes: i })}
                                        menu={[
                                            { id: 'asset-menu-0', name: labels.Platform_AssetWorX_Add, onClick: i => this.handleAdd(i) },
                                            { id: 'asset-menu-1', name: labels.Platform_AssetWorX_Edit, onClick: i => this.handleEdit(i) },
                                            { id: 'asset-menu-2', name: labels.Platform_AssetWorX_Import, onClick: i => this.handleImport(i) },
                                            { id: 'asset-menu-3', name: labels.Platform_AssetWorX_Delete, onClick: i => this.handleTryDelete(i) },
                                        ]}
                                        expandedNodeId={this.state.expandedNodeId}
                                        onGetNodes={parentId => this.handleGetNodes(parentId)}
                                        onGetAncestorNodes={i => this.handleGetAncestorNodes(i)}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>

                <ConfirmDialog
                    show={this.state.showConfirmDelete}
                    header={labels.Platform_ConfirmDelete_Title}
                    content={labels.Platform_Node_ConfirmDeleteMsg}
                    okButtonLabel={labels.Platform_CommonAction_Delete}
                    confirmButtonColor='error'
                    confirmButtonIcon={<SerendieSymbol name='trash' />}
                    onConfirm={() => this.onDeleteConfirm()}
                    onClose={() => this.setState({ showConfirmDelete: false })}
                />
                <NodesImportDialog
                    open={this.state.showImportDialog}
                    nodeId={this.state.importNodeId}
                    nodes={this.state.importNodes}
                    onClose={() => this.handleAlertClose()}
                />
            </>
        );
    }
}


export default withRouting(NodesTreeView);

export interface INodesTreeViewProps extends IRouterProps { }

export interface INodesTreeViewState
{
    importNodeId: number;
    importNodes: {label: string, id: number}[]
    nodes: IAssetWorXPlusTreeViewItemWithChildren[];
    selectedNode?: IAssetWorXPlusTreeViewItem;
    showConfirmDelete: boolean;
    showImportDialog: boolean;
    selectedNodeId?: string;
    refreshTreeToken: string;
    expandedNodeId?: number;
}
